import { useState, useEffect, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { channelLabels } from "@/lib/sectorVisibility";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  FileSpreadsheet, Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, Boxes, ShieldCheck, Trash2,
} from "lucide-react";

const CONFLICT_LABELS = {
  duplicate_sku: "Mükerrer SKU", wrong_prefix: "Yanlış önek", out_of_block: "Blok dışı SKU",
  invalid_unit: "Geçersiz birim", missing_kdv: "Eksik KDV", manual_kdv: "Manuel KDV kontrolü",
  no_category: "Kategorisiz",
};

function Stat({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-800", green: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`tabular text-lg font-black ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function ChannelCard({ c }) {
  const { activeSectorObj } = useAuth();
  const chLabels = channelLabels(activeSectorObj);
  const cc = c.conflict_counts || {};
  const realConflicts = ["duplicate_sku", "wrong_prefix", "out_of_block", "invalid_unit", "missing_kdv"]
    .reduce((s, k) => s + (cc[k] || 0), 0);
  return (
    <div data-testid={`dry-channel-${c.channel}`} className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-base font-bold text-slate-800">
          <Boxes className="h-4 w-4 text-brand-green" /> {c.channel === "market" ? chLabels.market : chLabels.toptan}
        </h3>
        {c.checksum_ok
          ? <span data-testid={`checksum-ok-${c.channel}`} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> Kontrol toplamı doğru</span>
          : <span data-testid={`checksum-fail-${c.channel}`} className="flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-bold text-red-700"><XCircle className="h-3.5 w-3.5" /> Sayı uyuşmuyor</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="Ürün" value={c.product_count} tone={c.checksum_ok ? "green" : "red"} />
        <Stat label="Beklenen" value={c.expected ?? "—"} />
        <Stat label="Kategori" value={c.categories} />
        <Stat label="Manuel KDV" value={c.manual_kdv_count} tone={c.manual_kdv_count ? "amber" : "slate"} />
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
        {realConflicts === 0
          ? <span className="rounded-md bg-emerald-50 px-2 py-1 font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">Kritik çakışma yok</span>
          : Object.entries(cc).filter(([k, v]) => v > 0 && k !== "manual_kdv").map(([k, v]) => (
            <span key={k} className="rounded-md bg-red-50 px-2 py-1 font-semibold text-red-700 ring-1 ring-inset ring-red-200">{CONFLICT_LABELS[k] || k}: {v}</span>
          ))}
        <span className="rounded-md bg-slate-100 px-2 py-1 text-slate-600">Dolu fiyat: piyasa {(c.filled_prices?.market || 0) + (c.filled_prices?.market_top || 0)} · satış {c.filled_prices?.sales || 0}</span>
      </div>
    </div>
  );
}

const FilePick = ({ id, label, file, onPick }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <label className="mb-1 block text-sm font-semibold text-slate-700">{label}</label>
    <input data-testid={`import-${id}-file`} type="file" accept=".xlsx"
      onChange={(e) => onPick(e.target.files?.[0] || null)}
      className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-white hover:file:bg-brand-green-600" />
    {file && <p className="mt-1 text-xs text-emerald-600">{file.name}</p>}
  </div>
);

export default function ImportWizard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [marketFile, setMarketFile] = useState(null);
  const [toptanFile, setToptanFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dry, setDry] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [restoreTs, setRestoreTs] = useState(null);
  const [restoring, setRestoring] = useState(false);

  const loadHistory = useCallback(() => {
    if (!isAdmin) return;
    api.get("/products/import/history").then((r) => setHistory(r.data.items || [])).catch(() => {});
  }, [isAdmin]);
  useEffect(() => { loadHistory(); }, [loadHistory, result]);

  const doRestore = async (ts) => {
    setRestoring(true);
    try {
      const { data } = await api.post("/products/import/restore", { ts, confirm: "ONAYLA" });
      toast.success(`Geri yüklendi — ${data.restored_products} ürün, ${data.restored_categories} kategori`);
      setRestoreTs(null); loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Geri yükleme başarısız"); }
    finally { setRestoring(false); }
  };

  const pickMarket = (f) => { setMarketFile(f); setDry(null); setResult(null); };
  const pickToptan = (f) => { setToptanFile(f); setDry(null); setResult(null); };


  const fd = () => {
    const f = new FormData();
    if (marketFile) f.append("market_file", marketFile);
    if (toptanFile) f.append("toptan_file", toptanFile);
    return f;
  };

  const runDryRun = async () => {
    if (!marketFile && !toptanFile) return toast.error("En az bir Excel (.xlsx) dosyası seçin");
    setBusy(true); setDry(null); setResult(null);
    try {
      const { data } = await api.post("/products/import/dry-run", fd(), { headers: { "Content-Type": "multipart/form-data" } });
      setDry(data);
      toast.success(data.can_commit ? "Analiz tamam — içe aktarmaya hazır" : "Analiz tamam — kontrol toplamı uyuşmuyor");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Analiz başarısız"); }
    finally { setBusy(false); }
  };

  const runCommit = async () => {
    if (confirmText.trim().toLocaleUpperCase("tr") !== "ONAYLA") return toast.error('Onay için kutuya "ONAYLA" yazın');
    setBusy(true);
    try {
      const f = fd(); f.append("confirm", "ONAYLA");
      const { data } = await api.post("/products/import/commit", f, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); setDry(null); setConfirmText("");
      toast.success(`İçe aktarma tamam — ${data.products_created} ürün, ${data.categories_created} kategori`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "İçe aktarma başarısız"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="import-wizard">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-slate-800">
          <FileSpreadsheet className="h-5 w-5 text-brand-green" /> Ürün / Kategori İçe Aktarma Sihirbazı
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Market ve Toptan Excel dosyalarını yükleyin. Önce <b>analiz (dry-run)</b> çalışır — hiçbir şey silinmez.
          Yalnızca <b>Yönetici</b> onaylayıp içe aktarabilir; onaylanınca ilgili kanalların mevcut ürün/kategorileri
          yedeklenip Excel'den yeniden kurulur.
        </p>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <FilePick id="market" label="Market Excel (.xlsx)" file={marketFile} onPick={pickMarket} />
          <FilePick id="toptan" label="Toptan Excel (.xlsx)" file={toptanFile} onPick={pickToptan} />
        </div>

        <Button data-testid="import-dryrun-btn" onClick={runDryRun} disabled={busy || (!marketFile && !toptanFile)}
          className="mt-4 bg-brand-green text-white hover:bg-brand-green-600">
          {busy && !dry ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />} Analiz Et (Dry-run)
        </Button>
      </div>

      {dry && (
        <div data-testid="dry-run-report" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-bold text-slate-800">Analiz Raporu</h3>
            {dry.can_commit
              ? <span data-testid="dry-can-commit" className="flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> İçe aktarmaya hazır</span>
              : <span data-testid="dry-cannot-commit" className="flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700"><AlertTriangle className="h-4 w-4" /> Kontrol toplamı uyuşmuyor</span>}
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Stat label="Yüklenecek ürün" value={dry.incoming_total} tone="green" />
            <Stat label="Silinecek ürün" value={dry.existing_to_delete?.products} tone="red" />
            <Stat label="Silinecek kategori" value={dry.existing_to_delete?.categories} tone="red" />
          </div>

          <div className="space-y-3">
            {(dry.channels || []).map((c) => <ChannelCard key={c.channel} c={c} />)}
          </div>

          <div className="mt-4 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 ring-1 ring-inset ring-amber-200">
            <p className="flex items-center gap-1.5 font-semibold"><AlertTriangle className="h-4 w-4" /> Kurallar</p>
            <ul className="ml-5 mt-1 list-disc space-y-0.5">
              <li>Tüm ürünlerin stok ve satılan miktarı <b>0</b> yüklenir.</li>
              <li>Boş fiyat = "fiyat yok"; ürün <b>Fiyatsız</b> görünür (0 yazılmaz).</li>
              <li>Excel SKU'ları değişmez; yalnız boşluk temizlenir + büyük harf yapılır.</li>
              <li>Onaylanınca ilgili kanalların mevcut ürün/kategorileri silinip yeniden kurulur (yedek alınır).</li>
            </ul>
          </div>

          {isAdmin ? (
            <div className="mt-5 rounded-xl border-2 border-red-200 bg-red-50/50 p-4">
              <p className="flex items-center gap-1.5 text-sm font-bold text-red-700"><Trash2 className="h-4 w-4" /> Kalıcı içe aktarma</p>
              <p className="mt-1 text-xs text-slate-600">
                Onaylamak için kutuya <b>ONAYLA</b> yazın. Mevcut ürün/kategoriler silinip Excel'den yeniden kurulacaktır.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input data-testid="import-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="ONAYLA" disabled={!dry.can_commit}
                  className="w-40 rounded-md border border-slate-300 px-3 py-2 text-sm uppercase tracking-widest disabled:opacity-50" />
                <Button data-testid="import-commit-btn" onClick={runCommit} disabled={busy || !dry.can_commit || confirmText.trim().toLocaleUpperCase("tr") !== "ONAYLA"}
                  className="bg-red-600 text-white hover:bg-red-700">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} İçe Aktar (Kalıcı)
                </Button>
              </div>
            </div>
          ) : (
            <p className="mt-4 rounded-md bg-slate-100 p-3 text-xs text-slate-500">İçe aktarma yalnızca Yönetici tarafından onaylanabilir. Bu bir salt-okunur önizlemedir.</p>
          )}
        </div>
      )}

      {result && (
        <div data-testid="import-result" className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-emerald-800"><CheckCircle2 className="h-5 w-5" /> İçe aktarma tamamlandı</h3>
          <div className="mt-3 flex flex-wrap gap-2">
            <Stat label="Yüklenen ürün" value={result.products_created} tone="green" />
            <Stat label="Yüklenen kategori" value={result.categories_created} tone="green" />
            <Stat label="Silinen ürün" value={result.deleted?.products} />
            <Stat label="Toplam ürün (DB)" value={result.total_products} />
            <Stat label="Manuel KDV listesi" value={result.manual_kdv?.length || 0} tone="amber" />
          </div>
          <p className="mt-3 text-xs text-slate-600">Yedek: <b>{result.backup?.products || 0}</b> ürün / <b>{result.backup?.categories || 0}</b> kategori
            (<span className="font-mono">products_import_backup_{result.backup?.ts}</span>).</p>
        </div>
      )}

      {isAdmin && (
        <div data-testid="import-history" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="flex items-center gap-2 font-heading text-base font-bold text-slate-800">
            <Boxes className="h-5 w-5 text-slate-400" /> İçe Aktarma Geçmişi
          </h3>
          <p className="mt-1 text-xs text-slate-500">Her içe aktarma öncesi mevcut veriler yedeklenir. Gerekirse bir işlemi yedeğinden geri alabilirsiniz.</p>
          {history.length === 0 ? (
            <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-400" data-testid="import-history-empty">Henüz kayıt yok.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {history.map((h) => (
                <div key={h.id} data-testid={`import-history-${h.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2.5">
                  <div className="text-xs text-slate-600">
                    <span className={`mr-2 rounded px-1.5 py-0.5 font-bold ${h.action === "restore" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {h.action === "restore" ? "Geri Yükleme" : "İçe Aktarma"}
                    </span>
                    <span className="font-mono">{(h.at || "").slice(0, 19).replace("T", " ")}</span>
                    <span className="mx-1 text-slate-300">·</span>
                    <b>{h.products_created ?? 0}</b> ürün / <b>{h.categories_created ?? 0}</b> kategori
                    <span className="mx-1 text-slate-300">·</span>
                    {(h.channels || []).join(", ")}
                    <span className="mx-1 text-slate-300">·</span>
                    <span className="text-slate-400">{h.by}</span>
                  </div>
                  {h.restorable ? (
                    restoreTs === h.backup_ts ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-red-600">Emin misiniz?</span>
                        <Button data-testid={`restore-confirm-${h.id}`} size="sm" disabled={restoring} onClick={() => doRestore(h.backup_ts)} className="bg-red-600 text-white hover:bg-red-700">
                          {restoring ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : null} Evet, Geri Yükle
                        </Button>
                        <Button data-testid={`restore-cancel-${h.id}`} size="sm" variant="outline" onClick={() => setRestoreTs(null)}>Vazgeç</Button>
                      </div>
                    ) : (
                      <Button data-testid={`restore-btn-${h.id}`} size="sm" variant="outline" onClick={() => setRestoreTs(h.backup_ts)}
                        className="border-red-200 text-red-600 hover:bg-red-50">
                        <Trash2 className="mr-1 h-3.5 w-3.5" /> Yedekten Geri Yükle
                      </Button>
                    )
                  ) : (
                    <span className="text-[11px] text-slate-400">Yedek yok</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
